import {
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Library,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation, useParams } from '#router'
import { activatableRowProps } from '@/components/admin/shared/activatable-row'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { IconField } from '@/components/admin/shared/icon-field'
import {
  containerFallbackIcon,
  leafKindIcon,
} from '@/components/admin/shared/kind-icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { buildContainerTree, mergeChildren } from '@/lib/coaching/source-tree'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { StudyPlansSection } from '../shared/study-plans-section'
import {
  useReorderChildren,
  useSourceContent,
  useSources,
} from './hooks/use-sources'
import { SourceContentModal } from './source-content-modal'
import type {
  Source,
  SourceCategory,
  SourceContent,
  SourceContentSummary,
  SourceContentSummaryKind,
} from './types'
import {
  SOURCE_CATEGORIES,
  SOURCE_CONTENT_ITEM_TYPES,
  SOURCE_CONTENT_TYPES,
} from './types'

export function SourceEditor() {
  const params = useParams()
  const { navigate } = useLocation()
  const sourceId = params.id as string

  const { getSource, updateSource, deleteSource } = useSources()
  const {
    contents,
    summary,
    loading: contentsLoading,
    deleteContent,
  } = useSourceContent(sourceId)

  const [source, setSource] = useState<Source | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Form state for source metadata
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    icon: '',
    category: 'prep_resource' as SourceCategory,
    url: '',
    isPublishable: true, // Default to publishable (original content)
  })

  // Content modal state
  const [showContentModal, setShowContentModal] = useState(false)
  const [editingContent, setEditingContent] = useState<SourceContent | null>(
    null,
  )
  const [modalParentId, setModalParentId] = useState<string | null>(null)

  // Expanded content items
  const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set())

  const utils = trpc.useUtils()
  const { reorderChildren, invalidateChildViews, isReordering } =
    useReorderChildren()
  const [draggedContent, setDraggedContent] = useState<SourceContent | null>(
    null,
  )

  // Load source on mount (and whenever sourceId changes). The custom router
  // reuses this component instance across `/admin/sources/:id` navigations, so
  // without a guard a slower response for a previous sourceId could resolve
  // last and overwrite the newer source's data — a real corruption risk on
  // Save. `ignore` is flipped in the cleanup so only the latest request wins.
  useEffect(() => {
    let ignore = false
    const loadSource = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await getSource(sourceId)
        if (ignore) return
        setSource(data)
        setFormData({
          name: data.name,
          slug: data.slug,
          icon: data.icon || '',
          category: data.category,
          url: data.url || '',
          isPublishable: data.isPublishable ?? true,
        })
      } catch (err) {
        if (ignore) return
        setError(err instanceof Error ? err.message : 'Failed to load source')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    loadSource()
    return () => {
      ignore = true
    }
  }, [sourceId, getSource])

  // A company-backed source has no stored name — it derives one from the
  // linked company, and the router rejects any attempt to write it. The Name
  // field is therefore read-only and stays out of the change check.
  const isCompanyLinked = source?.companyId != null

  const hasChanges =
    source !== null &&
    ((!isCompanyLinked && formData.name !== source.name) ||
      formData.slug !== source.slug ||
      formData.icon !== (source.icon || '') ||
      formData.category !== source.category ||
      formData.url !== (source.url || '') ||
      formData.isPublishable !== (source.isPublishable ?? true))

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const updated = await updateSource(sourceId, {
        ...(isCompanyLinked ? {} : { name: formData.name }),
        slug: formData.slug,
        icon: formData.icon || null,
        category: formData.category,
        url: formData.url || null,
        isPublishable: formData.isPublishable,
      })
      // `update` returns the raw stored column with no company join, so it
      // cannot report companyId or the derived display name. Carry both
      // forward from the loaded source instead of letting a save blank them.
      const next: Source = {
        ...updated,
        companyId: source?.companyId ?? updated.companyId,
        name: isCompanyLinked ? (source?.name ?? updated.name) : updated.name,
      }
      setSource(next)
      // Re-seed the form from what the server actually stored. The update
      // input trims `name`/`slug`, so a value saved with surrounding
      // whitespace comes back normalized — leaving formData as typed would
      // keep hasChanges true right after a successful save.
      setFormData({
        name: next.name,
        slug: next.slug,
        icon: next.icon || '',
        category: next.category,
        url: next.url || '',
        isPublishable: next.isPublishable ?? true,
      })
      toast.success('Source updated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this source?')) return
    try {
      await deleteSource(sourceId)
      toast.success('Source deleted')
      navigate('/admin/sources')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  const handleAddContent = () => {
    setEditingContent(null)
    setModalParentId(null)
    setShowContentModal(true)
  }

  const handleEditContent = (content: SourceContent) => {
    setEditingContent(content)
    setModalParentId(content.parentId ?? null)
    setShowContentModal(true)
  }

  const handleDeleteContent = async (content: SourceContent) => {
    if (!confirm(`Delete "${content.title}"?`)) return
    try {
      await deleteContent(content.id)
      toast.success('Content deleted')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  const toggleExpanded = useCallback((id: string) => {
    setExpandedContent((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const childrenMap = useMemo(() => buildContainerTree(contents), [contents])
  const rootContents = childrenMap.get(null) ?? []

  const handleAddChild = useCallback((parentId: string) => {
    setEditingContent(null)
    setModalParentId(parentId)
    setShowContentModal(true)
  }, [])

  /**
   * Sibling-scope drag: the dragged container is moved to the drop target's
   * position within the SAME parent. The mutation requires the complete mixed
   * sibling sequence, so for nested parents the parent's item memberships are
   * fetched and interleaved before submitting.
   */
  const handleSiblingDrop = useCallback(
    async (target: SourceContent) => {
      const dragged = draggedContent
      setDraggedContent(null)
      if (isReordering) return
      if (!dragged || dragged.id === target.id) return
      const parentKey = dragged.parentId ?? null
      if ((target.parentId ?? null) !== parentKey) {
        toast.error('Drag within the same level (use Edit to change parent)')
        return
      }
      try {
        const siblings = childrenMap.get(parentKey) ?? []
        let itemRefs: { id: string; order: number; createdAt: string }[] = []
        if (parentKey !== null) {
          const res = await utils.admin.content.listSourceContentItems.fetch({
            sourceContentId: parentKey,
          })
          itemRefs = (res?.data ?? []).map(
            (item: { id: string; order: number; createdAt: string }) => ({
              id: item.id,
              order: item.order,
              createdAt: item.createdAt,
            }),
          )
        }
        const sequence = mergeChildren(siblings, itemRefs).map((entry) => ({
          type: entry.type,
          id: entry.value.id,
        }))
        const from = sequence.findIndex(
          (e) => e.type === 'container' && e.id === dragged.id,
        )
        const to = sequence.findIndex(
          (e) => e.type === 'container' && e.id === target.id,
        )
        if (from === -1 || to === -1) return
        const [moved] = sequence.splice(from, 1)
        sequence.splice(to, 0, moved)
        await reorderChildren({
          sourceId,
          parentId: parentKey,
          children: sequence,
        })
        toast.success('Reordered')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to reorder')
        invalidateChildViews()
      }
    },
    [
      draggedContent,
      childrenMap,
      utils,
      reorderChildren,
      sourceId,
      invalidateChildViews,
      isReordering,
    ],
  )

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading source...</span>
        </div>
      </div>
    )
  }

  if (error || !source) {
    return (
      <div className="min-h-screen">
        <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
          <div className="mx-auto max-w-6xl px-6 py-4">
            <Button variant="ghost" onClick={() => navigate('/admin/sources')}>
              <ArrowLeft size={18} className="mr-2" />
              Back to Sources
            </Button>
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-6 py-6">
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Library size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              {error || 'Source not found'}
            </h3>
            <Button
              onClick={() => navigate('/admin/sources')}
              variant="outline"
            >
              Back to Sources
            </Button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/sources')}
              >
                <ArrowLeft size={18} className="mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 p-2 text-lg text-white">
                  <AuthoredIcon icon={source.icon} fallback={Library} />
                </div>
                <div>
                  <h1 className="text-foreground text-xl font-bold">
                    {source.name}
                  </h1>
                  <p className="text-muted-foreground text-sm">{source.slug}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleDelete}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 size={18} className="mr-2" />
                Delete
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
                <Save size={18} className="mr-2" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-6 py-6">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Metadata Section */}
          <div className="lg:col-span-1">
            {/* Content Summary Widget */}
            <ContentSummaryWidget summary={summary} loading={contentsLoading} />

            <div className="bg-card border-border rounded-lg border p-6">
              <h2 className="text-foreground mb-4 text-lg font-semibold">
                Details
              </h2>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="edit-name"
                    className="text-muted-foreground mb-1 block text-sm font-medium"
                  >
                    Name
                  </label>
                  <Input
                    id="edit-name"
                    type="text"
                    value={formData.name}
                    disabled={isCompanyLinked}
                    aria-describedby={
                      isCompanyLinked ? 'edit-name-hint' : undefined
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                  {isCompanyLinked && (
                    <p
                      id="edit-name-hint"
                      className="text-muted-foreground mt-1 text-xs"
                    >
                      Derived from the linked company — rename the company to
                      change it.
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="edit-slug"
                    className="text-muted-foreground mb-1 block text-sm font-medium"
                  >
                    Slug
                  </label>
                  <Input
                    id="edit-slug"
                    type="text"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="edit-category"
                    className="text-muted-foreground mb-1 block text-sm font-medium"
                  >
                    Category
                  </label>
                  <select
                    id="edit-category"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        category: e.target.value as SourceCategory,
                      })
                    }
                    className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                  >
                    {SOURCE_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <IconField
                    label="Icon"
                    value={formData.icon}
                    onChange={(icon) => setFormData({ ...formData, icon })}
                  />
                </div>

                <div>
                  <label
                    htmlFor="edit-url"
                    className="text-muted-foreground mb-1 block text-sm font-medium"
                  >
                    URL
                  </label>
                  <Input
                    id="edit-url"
                    type="url"
                    value={formData.url}
                    onChange={(e) =>
                      setFormData({ ...formData, url: e.target.value })
                    }
                    placeholder="https://..."
                  />
                </div>

                {/* Publishing Permission */}
                <div className="border-border border-t pt-4">
                  <div className="flex items-start gap-3">
                    <input
                      id="edit-isPublishable"
                      type="checkbox"
                      checked={formData.isPublishable}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isPublishable: e.target.checked,
                        })
                      }
                      className="mt-1 rounded"
                    />
                    <div>
                      <label
                        htmlFor="edit-isPublishable"
                        className="text-foreground block text-sm font-medium"
                      >
                        Allow Publishing
                      </label>
                      <p className="text-muted-foreground text-xs">
                        {formData.isPublishable
                          ? 'Problems from this source can be published directly.'
                          : 'External content — problems must be adapted before publishing.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="lg:col-span-2">
            <div className="bg-card border-border rounded-lg border p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-foreground text-lg font-semibold">
                  Content
                </h2>
                <Button size="sm" onClick={handleAddContent}>
                  <Plus size={16} className="mr-2" />
                  Add Content
                </Button>
              </div>

              {contentsLoading ? (
                <div className="text-muted-foreground flex items-center justify-center py-12">
                  <RefreshCw size={20} className="mr-2 animate-spin" />
                  Loading content...
                </div>
              ) : rootContents.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="bg-muted/50 mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
                    <BookOpen size={32} className="text-muted-foreground" />
                  </div>
                  <h4 className="text-foreground mb-2 font-medium">
                    No content yet
                  </h4>
                  <p className="text-muted-foreground mx-auto max-w-sm text-sm">
                    Add chapters, modules, or playlists to organize this
                    source's content.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {rootContents.map((content) => (
                    <ContentItem
                      key={content.id}
                      sourceId={sourceId}
                      content={content}
                      childrenMap={childrenMap}
                      expandedContent={expandedContent}
                      onToggle={toggleExpanded}
                      onEdit={handleEditContent}
                      onDelete={handleDeleteContent}
                      onAddChild={handleAddChild}
                      draggedId={draggedContent?.id ?? null}
                      onDragStart={setDraggedContent}
                      onDropOn={handleSiblingDrop}
                      onDragEnd={() => setDraggedContent(null)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Study Plans Section */}
            <div className="mt-6">
              <StudyPlansSection
                sourceId={sourceId}
                defaultIcon={source?.icon}
                defaultUrl={source?.url}
              />
            </div>
          </div>
        </div>
      </main>

      <SourceContentModal
        open={showContentModal}
        sourceId={sourceId}
        editingContent={editingContent}
        defaultParentId={modalParentId}
        allContents={contents}
        onClose={() => {
          setShowContentModal(false)
          setEditingContent(null)
          setModalParentId(null)
        }}
      />
    </div>
  )
}

// listSourceContent's summary never carries VIDEO/ARTICLE keys on read (those
// are create-only inputs that resolve to RESOURCE rows), so the summary
// widget only ever iterates the read-side kinds.
//
// `flatMap` rather than `filter` + a type predicate: a predicate's body is
// taken on trust, so the old one asserted a hand-maintained union it did
// nothing to establish. Here the narrowing is real — TypeScript derives the
// element type from the comparison itself and checks it against the
// annotation, so this cannot drift from `SourceContentSummaryKind`.
const SUMMARY_ITEM_TYPES: { value: SourceContentSummaryKind; label: string }[] =
  SOURCE_CONTENT_ITEM_TYPES.flatMap(({ value, label }) =>
    value === 'VIDEO' || value === 'ARTICLE' ? [] : [{ value, label }],
  )

interface ContentSummaryWidgetProps {
  summary: SourceContentSummary | null
  loading: boolean
}

function ContentSummaryWidget({ summary, loading }: ContentSummaryWidgetProps) {
  // Calculate total items
  const totalItems = summary
    ? Object.values(summary).reduce((acc, count) => acc + (count || 0), 0)
    : 0

  // Get items to display (only show types with counts > 0). Carries the count
  // through rather than re-reading `summary` in the map body, where the
  // non-null invariant established here would no longer be visible and had to
  // be asserted back with a `!`.
  const itemsToDisplay = summary
    ? SUMMARY_ITEM_TYPES.flatMap((type) => {
        const count = summary[type.value] ?? 0
        return count > 0 ? [{ ...type, count }] : []
      })
    : []

  return (
    <div className="bg-card border-border mb-6 rounded-lg border p-6">
      <h2 className="text-foreground mb-4 text-lg font-semibold">
        Content Summary
      </h2>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2 py-4 text-sm">
          <RefreshCw size={14} className="animate-spin" />
          Loading...
        </div>
      ) : totalItems === 0 ? (
        <p className="text-muted-foreground py-2 text-sm">
          No content items yet
        </p>
      ) : (
        <div className="space-y-3">
          {/* Total count */}
          <div className="border-border flex items-center justify-between border-b pb-3">
            <span className="text-muted-foreground text-sm font-medium">
              Total Items
            </span>
            <span className="text-foreground text-lg font-bold">
              {totalItems}
            </span>
          </div>

          {/* Breakdown by type */}
          <div className="space-y-2">
            {itemsToDisplay.map((type) => {
              const Icon = leafKindIcon(type.value)
              const count = type.count

              return (
                <div
                  key={type.value}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground text-sm">
                      {type.label}
                      {count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="bg-muted text-foreground rounded-full px-2.5 py-0.5 text-sm font-medium">
                    {count}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

interface ContentItemProps {
  sourceId: string
  content: SourceContent
  childrenMap: Map<string | null, SourceContent[]>
  expandedContent: Set<string>
  onToggle: (id: string) => void
  onEdit: (content: SourceContent) => void
  onDelete: (content: SourceContent) => void
  onAddChild: (parentId: string) => void
  draggedId: string | null
  onDragStart: (content: SourceContent) => void
  onDropOn: (target: SourceContent) => void
  onDragEnd: () => void
  depth?: number
}

function ContentItem({
  sourceId,
  content,
  childrenMap,
  expandedContent,
  onToggle,
  onEdit,
  onDelete,
  onAddChild,
  draggedId,
  onDragStart,
  onDropOn,
  onDragEnd,
  depth = 0,
}: ContentItemProps) {
  const { navigate } = useLocation()
  const children = childrenMap.get(content.id) ?? []
  const hasChildren = children.length > 0
  const isExpanded = expandedContent.has(content.id)

  const baseTypeLabel =
    SOURCE_CONTENT_TYPES.find((t) => t.value === content.kind)?.label ||
    content.kind
  // Orders are dense mixed-sequence indices (a drag renumbers all siblings;
  // containers skip indices over interleaved items), so the raw order is not
  // a meaningful ordinal. Use the position among same-KIND siblings instead
  // — the buckets are already comparator-sorted, so ordinal = display
  // position.
  const siblings = childrenMap.get(content.parentId ?? null) ?? []
  const sameKindOrdinal =
    siblings
      .filter((s) => s.kind === content.kind)
      .findIndex((s) => s.id === content.id) + 1
  const contentTypeLabel =
    sameKindOrdinal > 0 ? `${baseTypeLabel} ${sameKindOrdinal}` : baseTypeLabel

  const handleNavigateToDetail = () => {
    navigate(`/admin/sources/${sourceId}/content/${content.id}`)
  }

  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move'
          onDragStart(content)
        }}
        onDragOver={(e) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
        }}
        onDrop={(e) => {
          e.preventDefault()
          onDropOn(content)
        }}
        onDragEnd={onDragEnd}
        className={`group flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors ${
          draggedId === content.id
            ? 'bg-muted/50 opacity-50'
            : 'bg-muted/30 hover:bg-muted/50'
        }`}
        {...activatableRowProps({
          onActivate: handleNavigateToDetail,
          label: `Open ${content.title}`,
        })}
      >
        {/* Expand button — live at every depth */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onToggle(content.id)
          }}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${content.title}`}
          aria-expanded={hasChildren ? isExpanded : undefined}
          className={`text-muted-foreground rounded p-1 transition-colors ${
            hasChildren ? 'hover:bg-muted' : 'invisible'
          }`}
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <ContentThumbnail icon={content.icon} kind={content.kind} />

        {/* Content info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-foreground font-medium">{content.title}</span>
            <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs">
              {contentTypeLabel}
            </span>
            {content.estimatedMinutes != null &&
              content.estimatedMinutes > 0 && (
                <span className="text-muted-foreground text-xs">
                  {content.estimatedMinutes} min
                </span>
              )}
            {content.itemCount !== undefined && content.itemCount > 0 && (
              <span className="text-muted-foreground text-xs">
                • {content.itemCount} item
                {content.itemCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {content.description && (
            <p className="text-muted-foreground mt-0.5 text-sm line-clamp-1">
              {content.description}
            </p>
          )}
        </div>

        {/* Actions — hover OR keyboard-focus revealed */}
        <div className={cn(REVEAL_ON_HOVER, 'flex items-center gap-1')}>
          {content.url && (
            <a
              href={content.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground rounded p-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={14} />
            </a>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onAddChild(content.id)
            }}
            title="Add child container"
            className="text-muted-foreground hover:text-foreground rounded p-1.5"
          >
            <Plus size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onEdit(content)
            }}
            className="text-muted-foreground hover:text-foreground rounded p-1.5"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(content)
            }}
            aria-label={`Delete ${content.title}`}
            className="text-muted-foreground hover:text-destructive rounded p-1.5"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {isExpanded && hasChildren && (
        <div className="mt-1 space-y-1">
          {children.map((child) => (
            <ContentItem
              key={child.id}
              sourceId={sourceId}
              content={child}
              childrenMap={childrenMap}
              expandedContent={expandedContent}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              draggedId={draggedId}
              onDragStart={onDragStart}
              onDropOn={onDropOn}
              onDragEnd={onDragEnd}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The container/leaf's authored icon (emoji, bundled, or URL), or a mark
 * derived from its free-text kind — the same derivation
 * `ContainerRowIcon` uses in the library tree, so the two surfaces agree on
 * what a chapter, quiz, or section looks like when there's no authored icon.
 */
function ContentThumbnail({
  icon,
  kind,
}: {
  icon: string | null
  kind: string
}) {
  const Fallback = containerFallbackIcon(kind)
  return (
    <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded text-lg">
      <AuthoredIcon
        icon={icon}
        className="text-muted-foreground"
        fallback={Fallback}
      />
    </div>
  )
}
